// 手で登録するレシートの入力値と、API へ送る値の変換
//
// 時刻はレシート取り込みと同じく JST で扱い、分からなければ 00:00 JST（時刻不明の目印）で送る。
// 端末のタイムゾーンに依らず同じ値になるよう、+09:00 を明示して組み立てる。
import type { CardTransaction } from '@/api/types/card-transaction'
import type { CreateReceiptParams } from '@/api/types/receipt'
import { parseUtcDate } from '@/lib/date-utils'

export type ReceiptItemDraft = {
  name: string
  amount: string
}

export type ReceiptDraft = {
  store_name: string
  /** JST の YYYY-MM-DD */
  date: string
  /** JST の HH:mm。空なら時刻不明 */
  time: string
  total_amount: string
  payment_method: string
  notes: string
  items: ReceiptItemDraft[]
}

const JST_OFFSET_MS = 9 * 60 * 60 * 1000

/** バックエンドの RECEIPT_PAYMENT_METHODS と同じ並び */
export const PAYMENT_METHODS = ['現金', 'クレジット', '電子マネー', 'QRコード', '商品券', 'その他'] as const

/** UTC の ISO 文字列を JST の YYYY-MM-DD と HH:mm に分ける */
const toJstParts = (iso: string): { date: string; time: string } => {
  const jst = new Date(parseUtcDate(iso).getTime() + JST_OFFSET_MS).toISOString()
  return { date: jst.slice(0, 10), time: jst.slice(11, 16) }
}

export const emptyItemDraft = (): ReceiptItemDraft => ({ name: '', amount: '' })

export const emptyReceiptDraft = (date = ''): ReceiptDraft => ({
  store_name: '',
  date,
  time: '',
  total_amount: '',
  payment_method: '',
  notes: '',
  items: [emptyItemDraft()],
})

/** 滞在から開いたとき。日付は到着日、店名は場所名を入れておく（時刻は分からないので空） */
export const draftFromVisit = ({
  arrival_date,
  placeName,
}: {
  arrival_date: string | null
  placeName: string | null
}): ReceiptDraft => ({
  ...emptyReceiptDraft(arrival_date ? toJstParts(arrival_date).date : ''),
  store_name: placeName ?? '',
})

/** レシートの無いカード利用から開いたとき。時刻まで分かるのでカード側の値を使う */
export const draftFromCardTransaction = (tx: CardTransaction): ReceiptDraft => {
  const parts = tx.used_at ? toJstParts(tx.used_at) : { date: tx.used_date, time: '' }
  return {
    ...emptyReceiptDraft(parts.date),
    time: parts.time,
    store_name: tx.merchant,
    total_amount: String(tx.amount),
    payment_method: 'クレジット',
  }
}

/** 「1,280」のような区切り入りも受け付ける。数値にならなければ null */
const toAmount = (value: string): number | null => {
  const trimmed = value.replaceAll(',', '').trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? Math.round(parsed) : null
}

const toPurchasedAt = (date: string, time: string): string | null => {
  if (!date) return null
  const parsed = new Date(`${date}T${time || '00:00'}:00+09:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

export const toCreateReceiptParams = (
  draft: ReceiptDraft,
  visitId?: number,
  { useGoogle = true }: { useGoogle?: boolean } = {},
): CreateReceiptParams => ({
  store_name: draft.store_name.trim() || null,
  purchased_at: toPurchasedAt(draft.date, draft.time),
  total_amount: toAmount(draft.total_amount),
  payment_method: draft.payment_method || null,
  notes: draft.notes.trim() || null,
  visit_id: visitId ?? null,
  use_google: useGoogle,
  items: draft.items
    .map((item) => ({ name: item.name.trim(), amount: toAmount(item.amount) }))
    .filter((item) => item.name !== ''),
})
